/* Native Shopify form enhancement. No tracking, external requests or stored customer input. */
(() => {
  'use strict';
  if (customElements.get('bvj-contact-page')) return;
  class BvjContactPage extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.form = this.querySelector('form[data-contact-form]');
      this.topic = this.querySelector('[data-contact-topic-select]');
      this.bar = this.querySelector('[data-contact-sticky]');
      this.channels = this.querySelector('[data-contact-channels]');
      const params = new URLSearchParams(location.search);
      if (this.form && this.topic) {
        const allowed = Array.from(this.topic.options).map(option => option.value);
        const requested = params.get('onderwerp');
        if (allowed.includes(requested)) this.topic.value = requested;
        this.updateTopic();
        this.topic.addEventListener('change', () => this.updateTopic(), { signal });
        this.addEventListener('click', event => {
          const link = event.target.closest('a[data-contact-topic]');
          if (!link || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
          const value = link.dataset.contactTopic;
          if (!allowed.includes(value)) return;
          event.preventDefault();
          this.topic.value = value;
          this.updateTopic();
          const url = new URL(location.href);
          url.searchParams.set('onderwerp', value);
          url.hash = 'formulier';
          history.replaceState(null, '', url);
          this.querySelector('#formulier')?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
          this.topic.focus({ preventScroll: true });
        }, { signal });
        this.button = this.form.querySelector('[type=submit]');
        this.buttonMarkup = this.button?.innerHTML;
        this.form.addEventListener('submit', event => {
          if (this.submitting) { event.preventDefault(); return; }
          if (!this.form.checkValidity()) return;
          // Wait for Shopify's CAPTCHA listener. Do not lock a prevented submission.
          queueMicrotask(() => {
            if (event.defaultPrevented || !this.isConnected) return;
            this.submitting = true;
            if (this.button) {
              this.button.setAttribute('aria-disabled', 'true');
              this.button.textContent = 'Je vraag wordt verstuurd…';
            }
            this.resetTimer = setTimeout(() => this.resetSubmit(), 18000);
          });
        }, { signal });
      }
      this.applyContext(params);
      const status = this.querySelector('[data-contact-status]');
      if (status) { status.focus({ preventScroll: true }); status.scrollIntoView({ block: 'center' }); }
      window.addEventListener('pageshow', () => this.resetSubmit(), { signal });
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = 0; this.updateBar(); });
      };
      window.addEventListener('scroll', this.schedule, { passive: true, signal });
      window.addEventListener('resize', this.schedule, { passive: true, signal });
      document.addEventListener('focusin', this.schedule, { signal });
      document.addEventListener('focusout', this.schedule, { signal });
      document.addEventListener('toggle', this.schedule, { capture: true, signal });
      window.visualViewport?.addEventListener('resize', this.schedule, { passive: true, signal });
      if (window.IntersectionObserver && this.channels) {
        this.observer = new IntersectionObserver(this.schedule);
        this.observer.observe(this.channels);
      }
      this.mutation = new MutationObserver(this.schedule);
      this.mutation.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'open'] });
      document.addEventListener('visitorConsentCollected', this.schedule, { signal });
      this.updateBar();
    }
    updateTopic() {
      this.querySelectorAll('[data-contact-for]').forEach(group => {
        const shown = group.dataset.contactFor.split(' ').includes(this.topic.value);
        group.hidden = !shown;
        group.querySelectorAll('input,select').forEach(input => { input.disabled = !shown; });
      });
    }
    applyContext(params) {
      const title = (params.get('product') || '').slice(0, 220);
      const sku = (params.get('artikel') || '').slice(0, 100);
      const execution = (params.get('uitvoering') || '').slice(0, 220);
      const rawVariant = params.get('variant') || '';
      const variant = /^\d{1,24}$/.test(rawVariant) ? rawVariant : '';
      const path = params.get('productpad') || '';
      let cleanPath = '';
      try {
        const url = new URL(path, location.origin);
        if (path && url.origin === location.origin && /^\/products\/[a-z0-9-]+\/?$/i.test(url.pathname)) cleanPath = url.pathname + (variant ? '?variant=' + variant : '');
      } catch (error) { /* Ignore untrusted or malformed product destinations. */ }
      if (!title && !sku) return;
      const visible = [title, execution, sku ? 'Artikelnummer: ' + sku : ''].filter(Boolean);
      const lines = [...visible, variant ? 'Variant: ' + variant : '', cleanPath].filter(Boolean);
      const box = this.querySelector('[data-contact-context]');
      if (box) {
        box.hidden = false;
        box.querySelector('[data-contact-context-text]').textContent = visible.join(' · ');
        const input = box.querySelector('input');
        input.value = lines.join('\n');
        input.disabled = false;
        const back = box.querySelector('a');
        back.hidden = !cleanPath;
        if (cleanPath) back.href = cleanPath;
      }
      this.querySelectorAll('[data-contact-whatsapp]').forEach(link => {
        try {
          const url = new URL(link.href);
          url.searchParams.set('text', 'Hallo Badkamervoorjou, ik heb een vraag over ' + lines.join(' · ') + '.');
          link.href = url.href;
        } catch (error) { /* Retain the original link if a setting is invalid. */ }
      });
    }
    resetSubmit() {
      clearTimeout(this.resetTimer);
      this.submitting = false;
      if (this.button) { this.button.removeAttribute('aria-disabled'); this.button.innerHTML = this.buttonMarkup; }
    }
    updateBar() {
      if (!this.bar || !this.channels) return;
      const focused = document.activeElement?.matches('input,textarea,select,[contenteditable=true]');
      const keyboard = window.visualViewport && window.innerHeight - window.visualViewport.height > 140;
      const blocked = document.body.className.includes('overflow-hidden') || Array.from(document.querySelectorAll('dialog[open],header details[open],.section-header details[open],#shopify-pc__banner,.shopify-pc__banner,[aria-modal=true]')).some(element => element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
      this.bar.hidden = !(matchMedia('(max-width:749px)').matches && this.channels.getBoundingClientRect().bottom < 0 && !focused && !keyboard && !blocked);
    }
    disconnectedCallback() {
      this.abort?.abort(); this.observer?.disconnect(); this.mutation?.disconnect();
      cancelAnimationFrame(this.frame); clearTimeout(this.resetTimer); this.ready = false;
    }
  }
  customElements.define('bvj-contact-page', BvjContactPage);
})();
